<script setup>
import TvTeamName from '~/components/tv/TvTeamName.vue';
import {tvPages, tvRowsPerPage} from '~/utils/tvDisplay';

// Ladder rungs, top of the ladder first, as the website's ladder shows them:
// position, the last move and open challenges each way
const props = defineProps({
  panel: {type: Object, required: true},
  page: {type: Number, default: 0},
});
// wide, column or portrait (set by the panel's frame)
const mode = inject('tvMode', ref('wide'));

const rows = computed(() => tvPages(props.panel.ladder?.rows, tvRowsPerPage(props.panel, 'ladder', mode.value))[props.page] ?? []);
const columns = computed(() => {
  // One column when narrow (a column or a portrait screen); otherwise split evenly so a short ladder
  // fills both columns rather than one
  if (mode.value !== 'wide') return [rows.value];
  const half = Math.ceil(rows.value.length / 2);
  return [rows.value.slice(0, half), rows.value.slice(half)].filter(c => c.length);
});
</script>

<template>
  <div class="tv-ladder">
    <ol v-for="(column, c) in columns" :key="c" class="tv-ladder-col">
      <li v-for="row in column" :key="row.position + row.teamName" class="tv-rung">
        <span class="tv-rung-pos">{{ row.position }}</span>
        <span class="tv-rung-move">
          <v-icon v-if="row.oldPosition" :icon="row.position < row.oldPosition ? 'mdi-menu-up' : 'mdi-menu-down'"
                  :class="row.position < row.oldPosition ? 'tv-rung-move--up' : 'tv-rung-move--down'" size="30"/>
        </span>
        <span class="tv-rung-name">
          <tv-team-name :name="row.teamName"/>
          <v-icon v-if="row.frozen" icon="mdi-snowflake" size="20" class="tv-rung-frozen"/>
        </span>
        <span class="tv-rung-ch">
          <span v-if="row.outgoingChallenges" class="tv-rung-ch-out"><v-icon icon="mdi-arrow-top-right" size="22"/>{{ row.outgoingChallenges }}</span>
          <span v-if="row.incomingChallenges" class="tv-rung-ch-in"><v-icon icon="mdi-arrow-bottom-left" size="22"/>{{ row.incomingChallenges }}</span>
        </span>
      </li>
    </ol>
  </div>
</template>

<style scoped lang="scss">
.tv-ladder {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
}

.tv-ladder-col {
  list-style: none;
  margin: 0;
  padding: 0;
}

.tv-rung {
  display: grid;
  grid-template-columns: 36px 20px minmax(0, 1fr) auto;
  align-items: center;
  gap: 6px;
  height: 54px;
  padding: 0 20px;
  border-bottom: 1px solid var(--tv-line);
}



.tv-rung-pos {
  font-family: var(--tv-display);
  font-weight: 700;
  font-size: 28px;
  text-align: right;
  color: var(--tv-muted);
}



.tv-rung-name {
  display: flex;
  align-items: center;
  min-width: 0;
  padding-left: 6px;
  font-size: 22px;
  letter-spacing: -0.3px;
  color: var(--tv-ink);
}

:global(.tv-narrow .tv-ladder) {
  grid-template-columns: 1fr;
}

/* Last move and open challenges, coloured as on the website: up green, down */
/* red; outgoing in the club colour, incoming in green */
.tv-rung-move {
  display: flex;
  justify-content: center;
}

.tv-rung-move--up {
  color: #1e9e76;
}

.tv-rung-move--down {
  color: #e0243c;
}

.tv-rung-frozen {
  margin-left: 6px;
  color: #2477c8;
}

.tv-rung-ch {
  display: flex;
  gap: 14px;
  font-family: var(--tv-display);
  font-weight: 700;
  font-size: 22px;
}

.tv-rung-ch-out,
.tv-rung-ch-in {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.tv-rung-ch-out {
  color: var(--tv-accent-text);
}

.tv-rung-ch-in {
  color: #1e9e76;
}


</style>
